import type { Candidato } from './candidato.types';

/**
 * Separação LGPD do candidato em dois documentos com o mesmo id:
 * - `recruitment_candidatos/{id}` (ABERTO): nome, status, datas — qualquer um com role lê
 *   (gestores acompanham o andamento);
 * - `recruitment_candidatos_privado/{id}` (PROTEGIDO): estes campos — só Recrutamento,
 *   Chamados DP (si_rh) e Administrar SI leem (firestore.rules).
 * As telas continuam recebendo um `Candidato` inteiro (mesclarCandidato); quem não pode ver o
 * protegido recebe esses campos vazios.
 */
export const COLECAO_CANDIDATOS = 'recruitment_candidatos';
export const COLECAO_CANDIDATOS_PRIVADO = 'recruitment_candidatos_privado';

export const CAMPOS_PRIVADOS_CANDIDATO = [
  'email',
  'respostas',
  'respostasEtapa2',
  'linkEnviadoEm',
  'linkEnviadoPor',
  'linkCopiadoEm',
  'linkCopiadoPor',
  'linkEtapa2EnviadoEm',
  'linkEtapa2EnviadoPor',
  'linkEtapa2CopiadoEm',
  'linkEtapa2CopiadoPor',
] as const;

type CampoPrivado = (typeof CAMPOS_PRIVADOS_CANDIDATO)[number];
type Dados = Record<string, unknown>;

/** Documento aberto ainda com campo protegido (gravado antes da separação). */
export function temCamposPrivadosNoAberto(aberto: Dados): boolean {
  return CAMPOS_PRIVADOS_CANDIDATO.some((campo) => campo in aberto);
}

/** Os campos protegidos que ainda estão no documento aberto (pra mover pro protegido). */
export function camposPrivadosDoAberto(aberto: Dados): Partial<Record<CampoPrivado, unknown>> {
  const privados: Partial<Record<CampoPrivado, unknown>> = {};
  for (const campo of CAMPOS_PRIVADOS_CANDIDATO) {
    if (campo in aberto) privados[campo] = aberto[campo];
  }
  return privados;
}

/**
 * Junta aberto + protegido. O protegido vence; enquanto não migrou, vale o que ainda está no
 * aberto (formato antigo). `podeVerPrivado` false = campos protegidos vazios, mesmo se o aberto
 * antigo ainda os tiver (a tela não depende da regra pra esconder).
 */
export function mesclarCandidato(id: string, aberto: Dados, privado: Dados | undefined, podeVerPrivado: boolean): Candidato {
  const base: Dados = { id, ...aberto };
  for (const campo of CAMPOS_PRIVADOS_CANDIDATO) {
    const valor = podeVerPrivado ? (privado && campo in privado ? privado[campo] : aberto[campo]) : undefined;
    base[campo] = valor ?? (campo === 'email' ? '' : null);
  }
  return base as unknown as Candidato;
}
